/**
 * "Interview held, feedback not in yet" — candidates a recruiter may move on without the form.
 *
 * The Scheduling tab used to offer the next interview only after the write-up was submitted,
 * because the move happens from the Feedback tab and that tab lists a candidate only once their
 * feedback exists (Annika, Oct 2026: inviting straight after the interview meant every
 * interviewer had to submit the same day). This is the other way in: one button that moves the
 * stage in Lever and nothing else — no email, so the recruiter schedules the next interview the
 * usual way once the candidate shows up on the next round's list.
 *
 * The interviewer is still chased: the proxy keeps raising "feedback missing" for a round the
 * candidate has moved past (`earlierFeedbackOwed`), so the SLA tab's Nudge keeps working.
 *
 * Self-contained on purpose — it moves the stage and reloads the board by itself, so the
 * candidate appears on the next round without a manual refresh.
 */
import { useState } from 'react';
import { Button } from '@finn-auto/design-system/atoms/button';
import { Modal, ModalContent, ModalHeader, ModalTitle } from '@finn-auto/design-system/atoms/modal';
import { showToast } from '@finn-auto/design-system/atoms/toast';
import * as api from '../../services/taActionBoardApi';
import type { AwaitingFeedbackCandidate, RoleBoard } from '../../types/taActionBoard';
import { COLORS, fmtSlotStart } from './format';
import { ModalActions } from './ModalActions';
import { reportStageChange } from './boardAnalytics';

/** Open on the first visit; a recruiter who folds it keeps it folded in this browser. */
const COLLAPSE_KEY = 'ta-board-awaiting-feedback-collapsed';

function readCollapsed(): boolean {
  try {
    // window.localStorage, never the bare global — Node 25 shadows it under vitest (see boardCache.ts)
    return window.localStorage.getItem(COLLAPSE_KEY) === '1';
  } catch {
    return false;
  }
}

export function AwaitingFeedbackStrip({
  roles,
  boardEmail,
  onChanged,
}: {
  roles: RoleBoard[];
  boardEmail: string;
  /** Reload the board — the candidate is on the next round's list once it lands. */
  onChanged: () => void;
}) {
  const [asking, setAsking] = useState<AwaitingFeedbackCandidate | null>(null);
  const [busy, setBusy] = useState(false);
  const [collapsed, setCollapsed] = useState(readCollapsed);

  const toggle = () => {
    setCollapsed((v) => {
      try {
        window.localStorage.setItem(COLLAPSE_KEY, v ? '0' : '1');
      } catch {
        /* private mode — the toggle still works for this visit */
      }
      return !v;
    });
  };
  const groups = roles
    .map((role) => ({ role, rows: role.scheduling.awaitingFeedback ?? [] }))
    .filter((g) => g.rows.length > 0);
  if (groups.length === 0) return null;
  const total = groups.reduce((n, g) => n + g.rows.length, 0);

  const move = async (c: AwaitingFeedbackCandidate) => {
    setBusy(true);
    try {
      await api.postAction({
        type: 'move',
        opportunityId: c.opportunityId,
        boardEmail,
        targetStageId: c.nextStageId,
      });
      reportStageChange({
        fromStage: c.stageName,
        toStage: c.nextStageName,
        isRejection: false,
        withEmail: false,
      });
      showToast({
        status: 'success',
        description: `${c.name} moved to ${c.nextStageName}. The interviewers are still reminded for the feedback.`,
      });
      setAsking(null);
      onChanged();
    } catch (e) {
      showToast({
        status: 'error',
        description: e instanceof Error && e.message ? e.message : 'Move failed — nothing was changed.',
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      aria-label="Waiting on feedback"
      className="space-y-3 rounded-lg border border-gray-200 bg-white p-4"
    >
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={toggle}
          aria-expanded={!collapsed}
          className="flex items-center gap-2 rounded text-left text-sm font-semibold text-gray-900 hover:text-gray-600"
        >
          <span aria-hidden="true" className="w-3 text-xs text-gray-500">
            {collapsed ? '▸' : '▾'}
          </span>
          Interview held, feedback not in yet
        </button>
        <span
          className="rounded-full px-2.5 py-1 text-xs font-semibold"
          style={{ backgroundColor: COLORS.orangeBg, color: COLORS.orangeText }}
        >
          {total}
        </span>
      </div>
      {!collapsed && (
        <p className="text-xs text-gray-500">
          You can move them to the next stage now, without waiting for the form. Nothing is sent to the
          candidate; the interviewers are still reminded for the feedback on the SLA tab.
        </p>
      )}
      {!collapsed && groups.map(({ role, rows }) => (
        <div key={role.postingId} className="space-y-1.5">
          <div className="text-xs font-semibold text-gray-600">{role.roleTitle}</div>
          {rows.map((c) => (
            <div
              key={c.opportunityId}
              className="flex flex-wrap items-center gap-3 rounded-lg bg-gray-50 px-3 py-2"
            >
              <div className="min-w-0 flex-1">
                <div className="flex min-w-0 items-center gap-2">
                  <span className="truncate text-sm font-semibold text-gray-900">{c.name}</span>
                  <a
                    href={c.leverUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="shrink-0 text-xs text-blue-600 hover:underline"
                  >
                    Lever ↗
                  </a>
                </div>
                <div className="truncate text-xs text-gray-500">
                  {c.stageName} · {fmtSlotStart(c.interviewAt)}
                  {c.interviewers.length > 0 ? ` · waiting on ${c.interviewers.join(', ')}` : ''}
                </div>
              </div>
              <Button size="sm" variant="outline" onClick={() => setAsking(c)}>
                Move to {c.nextStageName}
              </Button>
            </div>
          ))}
        </div>
      ))}
      {asking && (
        <Modal open onOpenChange={(open: boolean) => !open && !busy && setAsking(null)}>
          <ModalContent className="!h-max !max-h-[calc(100dvh-2rem)] !w-[min(100vw-2rem,40rem)] gap-6 overflow-auto rounded p-6 md:p-10">
            <ModalHeader>
              <ModalTitle>
                Move {asking.name} to {asking.nextStageName}?
              </ModalTitle>
            </ModalHeader>
            <p className="text-sm text-gray-600">
              {asking.name} moves from “{asking.stageName}” to “{asking.nextStageName}” in Lever. No
              email is sent: once the board reloads they appear on the next round’s list, where you
              invite them as usual. The feedback for “{asking.stageName}” is still owed, and the
              interviewers can still be reminded from the SLA tab.
            </p>
            {/* A wrapper: the DS clones direct children named "Modal…" (see ActionModals). */}
            <div>
              <ModalActions
                confirmLabel={busy ? 'Moving…' : `Move to ${asking.nextStageName}`}
                confirmDisabled={busy}
                onConfirm={() => void move(asking)}
                onCancel={() => !busy && setAsking(null)}
              />
            </div>
          </ModalContent>
        </Modal>
      )}
    </section>
  );
}
